import { useContext, useMemo, useCallback } from "react";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import HolidayVillageIcon from "@mui/icons-material/HolidayVillage";
import ArticleIcon from "@mui/icons-material/Article";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";

import Template from "../../components/template";
import Loader from "../../components/loading";
import { Context } from "./Provider";

import EditFtm from "../components/editFtm";
import Logs from "../logs/each";
import EachFtmNotes from "../note/each";
import Chat from "../../components/userChat/chat";
import { commonSwal } from "../../components/swalAlert/swalAlert";
import { fetchFtmLabel } from "./service";
import FtmMap from "./components/FtmMap";
import Status from "./components/ftmStatus/Status";
import FtmDetails from "./components/ftm/FtmDetails";
import FtmInformation from "./components/ftm/Information";
import TrackerSettings from "./components/tracker/TrackerSettings";
import Skills from "./components/ftm/Skills";
import HolidaysCalendar from "./components/HolidaysCalendar";
import FtmAttachments from "../components/attachments";
import SpeedDialBtn from "../../components/speedDialBtn";
import PersonalDocuments from "./components/ftm/personalDocuments/PersonalDocuments";
import FooterBtn from "./components/FooterBtn";

const Html = () => {
    const { ftm_id, state, dispatch } = useContext(Context);

    const {
        ftm,
        is_loading,
        is_show_edit_ftm,
        ftm_edit_count,
        is_open_logs,
        is_open_ftm_note,
        is_show_chat,
        is_show_ftm_attachments,
    } = state;

    /** 🔹 Utility to simplify dispatch close handlers */
    const handleClose = useCallback(
        (type, payload = false) => () => dispatch({ type, [type.toLowerCase()]: payload }),
        [dispatch]
    );

    /** 🔹 Print Label handler */
    const printLabel = useCallback(() => {
        commonSwal({
            notificationType: "PRINT_LABEL",
            preConfirm: () => fetchFtmLabel(ftm_id),
        });
    }, [ftm_id]);

    /** 🔹 SpeedDial actions memoized */
    const speedDialActions = useMemo(
        () => [
            {
                icon: <AttachFileIcon />,
                name: "ATTACHMENTS",
                onClickFn: () => dispatch({ type: "IS_SHOW_FTM_ATTACHMENTS", is_show_ftm_attachments: true }),
            },
            {
                icon: <HolidayVillageIcon />,
                name: "HOLIDAYS",
                onClickFn: () => dispatch({ type: "IS_OPEN_TRADESPERSON_HOLIDAYS", is_open_tradesperson_holidays: true }),
            },
            {
                icon: <ArticleIcon />,
                name: "LOGS",
                onClickFn: () => dispatch({ type: "IS_OPEN_LOGS", is_open_logs: true }),
            },
            {
                icon: <DocumentScannerIcon />,
                name: "NOTES",
                onClickFn: () => dispatch({ type: "IS_OPEN_FTM_NOTE", is_open_ftm_note: true }),
            },
            {
                icon: <PrintIcon />,
                name: "PRINT LABEL",
                onClickFn: printLabel,
            },
        ],
        [dispatch, printLabel]
    );

    if (is_loading) return <Loader />;

    return (
        <>
            <Template
                footer={<SpeedDialBtn actions={speedDialActions} icon={<VisibilityIcon />} />}
            >
                <div className="container-fluid px-2 px-md-4">
                    <div className="row">
                        <div className="col-lg-8">
                            <div className="row">
                                <div className="col-xl-12 col-md-6 mb-xl-0 mb-4">
                                    <div className="card bg-transparent shadow-xl">
                                        <FtmMap />
                                    </div>
                                </div>
                                <div className="col-xl-12 col-md-6 mt-4">
                                    <Status />
                                </div>
                                <div className="col-md-12 mb-lg-0 mb-4">
                                    <FtmDetails />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <Skills />
                        </div>
                    </div>

                    <TrackerSettings />

                    <div className="row mt-4">
                        <div className="col-lg-7 col-12">
                            <div className="card">
                                <div className="card-header pb-0 px-3">
                                    <h6 className="mb-0">Information</h6>
                                </div>
                                <div className="card-body pt-4 p-3">
                                    <FtmInformation />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-5 col-12 mt-md-0 mt-4">
                            <PersonalDocuments />
                        </div>
                    </div>

                    <div className="card mt-4 mb-5">
                        <div className="card-body p-3">
                            <FooterBtn />
                        </div>
                    </div>
                </div>
            </Template>

            {/* 🔹 Modals / Drawers */}
            <HolidaysCalendar ftm_id={ftm_id} />

            <FtmAttachments
                ftm_id={ftm_id}
                open={is_show_ftm_attachments}
                handleClose={handleClose("IS_SHOW_FTM_ATTACHMENTS")}
            />

            <EditFtm
                ftm_id={ftm_id}
                open={is_show_edit_ftm}
                handleClose={handleClose("IS_SHOW_EDIT_FTM")}
                handleSubmitClose={() =>
                    dispatch({ type: "SET_FTM_EDIT_COUNT", ftm_edit_count: ftm_edit_count + 1 })
                }
            />

            <Logs ftm_id={ftm_id} open={is_open_logs} handleClose={handleClose("IS_OPEN_LOGS")} />

            <EachFtmNotes
                ftm_id={ftm_id}
                open={is_open_ftm_note}
                handleClose={handleClose("IS_OPEN_FTM_NOTE")}
            />

            <Chat
                user_name={`${ftm.ftm_forename} ${ftm.ftm_surname}`}
                user_firebase_document_id={ftm.ftm_firebase_document_id}
                show={is_show_chat}
                handleClose={handleClose("IS_SHOW_CHAT")}
            />
        </>
    );
};

export default Html;
